import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const ActivePanelContext = createContext({
  activePanelId: null,
  openPanel: () => {},
  closePanel: () => {},
});

export const ActivePanelProvider = ({ children }) => {
  const [activePanelId, setActivePanelId] = useState(null);

  const openPanel = useCallback((id) => setActivePanelId(id), []);
  const closePanel = useCallback(
    (id) => setActivePanelId((curr) => (id === undefined || curr === id ? null : curr)),
    []
  );

  return (
    <ActivePanelContext.Provider value={{ activePanelId, openPanel, closePanel }}>
      {children}
    </ActivePanelContext.Provider>
  );
};

export const useActivePanel = (id) => {
  const { activePanelId, openPanel, closePanel } = useContext(ActivePanelContext);
  const isOpen = activePanelId === id;
  const open = useCallback(() => openPanel(id), [openPanel, id]);
  const close = useCallback(() => closePanel(id), [closePanel, id]);
  return { isOpen, open, close };
};

/**
 * Combines a local "selected item" with the ActivePanel coordination, so that
 * opening one detail panel automatically closes any other registered panel.
 *
 * Usage:
 *   const { selected, select, deselect, isOpen } = useDetailPanel("team-lens");
 *   <Row onClick={() => select(item)} />
 *   <Sidebar isOpen={isOpen} onClose={deselect} {...selected} />
 */
export const useDetailPanel = (id) => {
  const { isOpen, open, close } = useActivePanel(id);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isOpen) setSelected(null);
  }, [isOpen]);

  const select = useCallback(
    (item) => {
      setSelected(item);
      open();
    },
    [open]
  );

  const deselect = useCallback(() => {
    setSelected(null);
    close();
  }, [close]);

  return { selected, select, deselect, isOpen };
};
